import React, { useState, useRef, useEffect } from "react";
import "./addassets.scss";
import { DatePicker, TimePicker } from "antd";

import BreadCrumsHeader from "../../common/BreadCrums";
import Input from "../../common/FormInputs";
import FormLayout from "../../common/FormLayout";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import { useFormik } from "formik";
import { assetsSchema } from "../../../utils/validationSchemas";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { combineDateTimeAndConvertToUTC } from "../../../utils/utilFunctions";
import { createAsset, editAsset } from "../../../services/assetsService";
import InputTextArea from "../../common/InputTextArea";
import Loader from "../../common/Loader";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import imgIcon from "../../../Assets/Icons/imgIcon.svg";
import Selector from "../../common/Selector";
import { getFundraisers } from "../../../services/fundraiserService";
import UploadExcel from "../../common/OfferForm/Location/UploadExcel";
import toast from "../../../utils/toast";
import { uploadFile, config as s3Config } from "../../../utils/s3";

const AddAssetsForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector((state) => state?.assetsReducer?.loading);
  const allAssets = useSelector((state) => state.assetsReducer.allPromotions);
  const { id = null } = location?.state || "";
  const navigate = useNavigate();
  const [fileData, setFileData] = useState();
  const [fundraisers, setFundraisers] = useState([]);
  const [fundraiserIds, setFundraiserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    postcards: null,
    a3Posters: null,
    a4Posters: null,
    socialMediaBanners: null,
    digitalFlyer: null,
    qrCodes: null,
    emailTemplates: null,
  });
  const [uploading, setUploading] = useState({
    postcards: false,
    a3Posters: false,
    a4Posters: false,
    socialMediaBanners: false,
    digitalFlyer: false,
    qrCodes: false,
    emailTemplates: false,
  });
  const fileInputRef = useRef(null);
  dayjs.extend(utc);
  dayjs.extend(tz);

  const timeZone = dayjs.tz.guess();

  const getFundarisersData = async () => {
    try {
      const result = await dispatch(getFundraisers()).unwrap();
      setFundraisers(result?.data);
    } catch (error) {
      console.error("Error fetching fundraisers", error);
      throw error;
    }
  };

  useEffect(() => {
    getFundarisersData();
  }, []);

  const initialValues = {
    title: "",
    assetDescription: "",
    selectedFundraisers: [],
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    document: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: assetsSchema,

    onSubmit: async (values) => {
      console.log("Offer values: ", values);
      const startDateTimeUTC = combineDateTimeAndConvertToUTC(
        values?.startDate,
        values?.startTime
      );
      const endDateTimeUTC = combineDateTimeAndConvertToUTC(
        values?.endDate,
        values?.endTime
      );
      if (id !== "" && id !== null) {
        // const formData = new FormData();
        // formData.append("id", id);
        // formData.append("title", values.title);
        // formData.append("description", values.assetDescription);
        // formData.append("startDateTime", startDateTimeUTC);
        // formData.append("endDateTime", endDateTimeUTC);
        // formData.append("file", fileUrl);
        // fundraiserIds.forEach((fundraiserId, index) => {
        //   formData.append("fundraisers", fundraiserId);
        // });

        const payload = {
          id: id,
          title: values?.title,
          description: values?.assetDescription,
          startDateTime: startDateTimeUTC,
          endDateTime: endDateTimeUTC,
          file: fileUrl,
          fundraisers: fundraiserIds,
        };

        const editAssetPromise = await dispatch(editAsset(payload))?.unwrap();
        if (editAssetPromise?.success === true) {
          navigate("/promotions-list");
        }
      } else {
        // Build payload from uploaded images if no zip is provided
        const entries = Object.entries(uploadedFiles).filter(([,v]) => Boolean(v));
        const images = entries.map(([,v]) => v.url);
        const firstUploadedKey = entries.length > 0 ? entries[0][0] : null;

        const primaryFile = fileUrl || (images.length > 0 ? images[0] : null);
        if (!primaryFile) {
          toast.error('Please provide the asset file');
          return;
        }

        const payload = {
          title: values?.title,
          description: values?.assetDescription,
          startDateTime: startDateTimeUTC,
          endDateTime: endDateTimeUTC,
          file: primaryFile,
          images: images && images.length > 0 ? images : undefined,
          category: firstUploadedKey || undefined,
          // Include both keys to satisfy backend variations
          fundraisers: fundraiserIds,
          fundraiser: fundraiserIds,
        };

        const createAssetPromise = await dispatch(
          createAsset(payload)
        )?.unwrap();
        if (createAssetPromise?.success === true) {
          navigate("/promotions-list");
        }
      }
    },
  });

  const {
    title,
    assetDescription,
    selectedFundraisers: rawSelectedFundraisers,
    startDate,
    startTime,
    endDate,
    endTime,
    // Avoid shadowing the global `document` object
    document: assetDocument,
  } = formik.values;

  // Ensure selectedFundraisers is always an array
  const selectedFundraisers = Array.isArray(rawSelectedFundraisers) ? rawSelectedFundraisers : [];

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    setValues,
  } = formik;

  const items = [
    {
      title: "Marketing Assets",
    },
    {
      title: "Add Marketing Assets",
    },
  ];

  useEffect(() => {
    if (id !== "" && id !== null && allAssets !== null) {
      allAssets?.map((asset) => {
        if (asset?._id === id) {
          const selectIds = asset?.fundraisers?.map((f) => f?._id) || [];
          setFundraiserIds(selectIds);
          setValues({
            title: asset?.title,
            assetDescription: asset?.description,
            startDate: dayjs.utc(asset?.startDateTime).tz(timeZone),
            startTime: dayjs.utc(asset?.startDateTime).tz(timeZone),
            endDate: dayjs.utc(asset?.endDateTime).tz(timeZone),
            endTime: dayjs.utc(asset?.endDateTime).tz(timeZone),
            document: asset?.file,
            selectedFundraisers: asset?.fundraisers?.map((f) => f?.user?.name) || [],
          });
          setFileUrl(asset?.file !== undefined ? asset?.file : "");
        }
      });
    }
  }, [id, allAssets]);

  const handleFundraisersChange = (values) => {
    const fundraisersArray = Array.isArray(fundraisers) ? fundraisers : [];
    let updatedFundraisers = Array.isArray(values) ? values : [];

    // Handle Select All
    if (updatedFundraisers.includes("Select All")) {
      updatedFundraisers = fundraisersArray
          .map((f) => f?.user?.name)
          .filter(Boolean);
        setSelectAll(true);
    } else {
        setSelectAll(false);
    }

    const updatedIds = fundraisersArray
      .filter((f) => updatedFundraisers.includes(f?.user?.name))
      .map((f) => f?._id)
      .filter(Boolean);

    setFieldValue("selectedFundraisers", updatedFundraisers);
    setFundraiserIds(updatedIds);
  };

  const handleMerchantDeselect = (removedFundraiser) => {
    const updatedFundraisers = selectedFundraisers.filter(
      (fundraiser) => fundraiser !== removedFundraiser
    );

    const fundraisersArray = Array.isArray(fundraisers) ? fundraisers : [];
    const updatedIds = fundraisersArray
      .filter((f) => updatedFundraisers.includes(f?.user?.name))
      .map((f) => f._id);

    setFieldValue("selectedFundraisers", updatedFundraisers);
    setFundraiserIds(updatedIds);
    setSelectAll(false);
  };

  const uniqueNames = new Set();

  const fundraiserOptions = [
    { value: "Select All", label: "Select All" },
    ...(Array.isArray(fundraisers) ? fundraisers
      .map((op) => {
        const name = op?.user?.name ?? "abc";
        if (!uniqueNames.has(name)) {
          uniqueNames.add(name);
          return {
            value: name,
            label: name,
          };
        }
        return null;
      })
      .filter((option) => option !== null) : []),
  ];

  const validateFile = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile == "zip" || extFile == "rar") {
      return true;
    } else {
      return false;
    }
  };

  const validateImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    return validTypes.includes(file.type);
  };

  const validateQrFile = (file) => {
    const validTypes = ['image/png', 'image/svg+xml'];
    return validTypes.includes(file.type);
  };

  const validateEmailTemplateFile = (file) => {
    const name = file?.name?.toLowerCase() || '';
    const ext = name.split('.').pop();
    const validExts = ['html', 'htm', 'zip'];
    const validTypes = ['text/html', 'application/zip', 'application/x-zip-compressed'];
    return validExts.includes(ext) || validTypes.includes(file.type);
  };

  const handleUpload = async (fileType, file) => {
    // Validate according to file type
    if (fileType === 'emailTemplates') {
      if (!validateEmailTemplateFile(file)) {
        toast.error('Please upload HTML or ZIP file for Email Templates');
        return;
      }
    } else if (fileType === 'qrCodes') {
      if (!validateQrFile(file)) {
        toast.error('Please upload a valid QR image (PNG or SVG only)');
        return;
      }
    } else {
      if (!validateImageFile(file)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG)');
        return;
      }
    }

    try {
      setUploading(prev => ({ ...prev, [fileType]: true }));
      // Upload file to S3 (returns key/filename). Build public URL for preview
      const s3Key = await uploadFile(file, file.name);
      const bucket = s3Config?.bucketName;
      const region = s3Config?.region;
      const dir = s3Config?.dirName ? `${s3Config.dirName}/` : "";
      const publicUrl = `https://${bucket}.s3.${region}.amazonaws.com/${dir}${s3Key}`;
      
      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: {
          file: file,
          url: publicUrl,
          name: file.name
        }
      }));

      const typeLabelMap = {
        postcards: "Postcards",
        a3Posters: "A3 Posters",
        a4Posters: "A4 Posters",
        socialMediaBanners: "Social Media Banners",
        digitalFlyer: "Digital Flyer",
        qrCodes: "QR Codes",
        emailTemplates: "Email Templates",
      };
      const label = typeLabelMap[fileType] || "File";
      toast.success(`${label} uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleCardClick = (fileType) => {
    if (uploading[fileType]) return;
    const input = document.createElement('input');
    input.type = 'file';
    if (fileType === 'emailTemplates') {
      input.accept = '.html,.htm,.zip';
    } else if (fileType === 'qrCodes') {
      input.accept = '.png,.svg';
    } else {
      input.accept = 'image/*';
    }
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleUpload(fileType, file);
      }
    };
    input.click();
  };

  const OfferInfoContent = (
    <div className="assetform">
      <Input
        type="text"
        label="Title"
        placeholder=""
        width="100%"
        radius="5px"
        InputDivClass="w-[100%] mt-8"
        name="title"
        value={title}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.title}
        touched={touched.title}
      />
      <InputTextArea
        label={"Asset Description"}
        divClassName="relative mt-5 w-full"
        labelClassName="invitationLabels"
        textareaClassName="w-full h-[150px] px-[1%] pt-2 rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins"
        name="assetDescription"
        value={assetDescription}
        handleBlur={handleBlur}
        error={errors.assetDescription}
        touched={touched.assetDescription}
        setFieldValue={setFieldValue}
      />
      <Selector
        value={selectedFundraisers}
        defaultValue={
          id !== "" && id !== null ? selectedFundraisers : undefined
        }
        label={"Fundraisers"}
        labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
        divClassName="mt-4 w-full multipleField"
        selectClassName="w-full rounded-[5px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none"
        flexClassName="gap-1"
        options={fundraiserOptions}
        suffixIcon={<DownArrow />}
        placeholder={"Select Fundraisers"}
        onChange={handleFundraisersChange}
        onDeselect={handleMerchantDeselect}
        handleBlur={handleBlur}
        error={errors.selectedFundraisers}
        touched={touched.selectedFundraisers}
        multiple={true}
      />

      <div className="flex sm:flex-col md:flex-row justify-between mt-8 w-[100%]">
        <div className="md:w-[46%] sm:w-[100%]">
          <p className="invitationLabels">Start Date</p>
          <DatePicker
            className="invitationInputs w-[100%] outline-none"
            dateFormat="yyyy-MM-dd"
            placeholder=""
            name="startDate"
            defaultValue={startDate}
            value={startDate}
            onChange={(date, dateString) => {
              setFieldValue("startDate", date);
            }}
            onBlur={handleBlur}
          />
          <span className="text-red-600 text-[12px] font-[600] ms-1">
            {errors.startDate && touched.startDate ? errors.startDate : null}
          </span>
        </div>
        <div className="md:w-[46%] sm:w-[100%] sm:mt-10 md:mt-0">
          <p className="invitationLabels">Start Time</p>
          <TimePicker
            className="invitationInputs w-[100%]"
            placeholder=""
            format="HH:mm"
            name="startTime"
            defaultValue={startTime}
            value={startTime}
            onChange={(date, dateString) => {
              setFieldValue("startTime", date);
            }}
            onBlur={handleBlur}
          />
          <span className="text-red-600 text-[12px] font-[600] ms-1">
            {errors.startTime && touched.startTime ? errors.startTime : null}
          </span>
        </div>
      </div>

      <div className="flex sm:flex-col md:flex-row justify-between mt-8 w-[100%]">
        <div className="md:w-[46%] sm:w-[100%]">
          <p className="invitationLabels">End Date</p>
          <DatePicker
            className="invitationInputs w-[100%] outline-none"
            dateFormat="yyyy-MM-dd"
            placeholder=""
            name="endDate"
            defaultValue={endDate}
            value={endDate}
            onChange={(date, dateString) => {
              setFieldValue("endDate", date);
            }}
            onBlur={handleBlur}
          />
          <span className="text-red-600 text-[12px] font-[600] ms-1">
            {errors.endDate && touched.endDate ? errors.endDate : null}
          </span>
        </div>
        <div className="md:w-[46%] sm:w-[100%] sm:mt-10 md:mt-0">
          <p className="invitationLabels">End Time</p>
          <TimePicker
            className="invitationInputs w-[100%]"
            placeholder=""
            format="HH:mm"
            name="endTime"
            defaultValue={endTime}
            value={endTime}
            onChange={(date, dateString) => {
              setFieldValue("endTime", date);
            }}
            onBlur={handleBlur}
          />
          <span className="text-red-600 text-[12px] font-[600] ms-1">
            {errors.endTime && touched.endTime ? errors.endTime : null}
          </span>
        </div>
      </div>
    </div>
  );

  const MediaContent = (
    <>
      {/* Line divider */}
      <div className="w-full h-[1px] bg-[#E5E5E5] my-6"></div>
      
      <div className="mt-6 mb-4">
        <p className="text-[16px] font-[500] text-[#000000] font-poppins">
          Upload images
        </p>
      </div>
      
      {/* Custom Upload Section */}
      <div
        className="max-w-2xl mx-auto rounded-[20px] p-6"
        style={{
          border: "2px dashed #00ADEF",
          boxShadow: "0px 4px 20px 10px #79797926",
        }}
      >
        <div className="grid grid-cols-2 gap-x-0 gap-y-4 mb-6">
                      {/* Postcards */}
            <div className="flex flex-col items-center -mr-12">
              <div 
                className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                  uploadedFiles.postcards ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
                onClick={() => handleCardClick('postcards')}
              >
                {uploading.postcards ? (
                  <>
                    <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                    <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                  </>
              ) : uploadedFiles.postcards ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.postcards.url} alt={uploadedFiles.postcards.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.postcards.name}</p>
                </>
              ) : (
                  <>
                    <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                    <p className="text-sm font-medium text-gray-400">Upload Image</p>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">Postcards: Standard: 1500 × 1050 pixels</p>
            </div>

            {/* A3 Posters */}
            <div className="flex flex-col items-center -ml-12">
              <div 
                className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                  uploadedFiles.a3Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
                onClick={() => handleCardClick('a3Posters')}
              >
                {uploading.a3Posters ? (
                  <>
                    <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                    <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                  </>
              ) : uploadedFiles.a3Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a3Posters.url} alt={uploadedFiles.a3Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a3Posters.name}</p>
                </>
              ) : (
                  <>
                    <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                    <p className="text-sm font-medium text-gray-400">Upload Image</p>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">A3 Posters: 3508 × 4961 pixels</p>
            </div>

            {/* A4 Posters */}
            <div className="flex flex-col items-center -mr-12">
              <div 
                className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                  uploadedFiles.a4Posters ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
                onClick={() => handleCardClick('a4Posters')}
              >
                {uploading.a4Posters ? (
                  <>
                    <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                    <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                  </>
              ) : uploadedFiles.a4Posters ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.a4Posters.url} alt={uploadedFiles.a4Posters.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.a4Posters.name}</p>
                </>
              ) : (
                  <>
                    <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                    <p className="text-sm font-medium text-gray-400">Upload Image</p>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">A4 Posters: 2480 × 3508 pixels</p>
            </div>

            {/* Social Media Banners */}
            <div className="flex flex-col items-center -ml-12">
              <div 
                className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                  uploadedFiles.socialMediaBanners ? 'border-green-400 bg-green-50' : 'border-gray-200'
                }`}
                onClick={() => handleCardClick('socialMediaBanners')}
              >
                {uploading.socialMediaBanners ? (
                  <>
                    <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                    <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                  </>
              ) : uploadedFiles.socialMediaBanners ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.socialMediaBanners.url} alt={uploadedFiles.socialMediaBanners.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.socialMediaBanners.name}</p>
                </>
              ) : (
                  <>
                    <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                    <p className="text-sm font-medium text-gray-400">Upload Image</p>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">Social Media Banners: 1080 × 1080 pixels</p>
            </div>
        </div>

        {/* Standard digital flyer - left aligned with caption below */}
        <div className="grid grid-cols-2 gap-x-4">
          <div className="flex flex-col items-center -mr-12">
            <div 
              className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                uploadedFiles.digitalFlyer ? 'border-green-400 bg-green-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('digitalFlyer')}
            >
              {uploading.digitalFlyer ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.digitalFlyer ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.digitalFlyer.url} alt={uploadedFiles.digitalFlyer.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.digitalFlyer.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                  <p className="text-sm font-medium text-gray-400">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">Standard digital flyer: 1920 × 1080 pixels</p>
          </div>
          {/* QR Codes */}
          <div className="flex flex-col items-center -ml-12">
            <div 
              className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                uploadedFiles.qrCodes ? 'border-green-400 bg-green-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('qrCodes')}
            >
              {uploading.qrCodes ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.qrCodes ? (
                <>
                  <div className="w-full h-[72px] mb-2 overflow-hidden rounded flex items-center justify-center">
                    <img src={uploadedFiles.qrCodes.url} alt={uploadedFiles.qrCodes.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.qrCodes.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                  <p className="text-sm font-medium text-gray-400">Upload Image</p>
                </>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">QR Codes (PNG/SVG)</p>
          </div>
        </div>

        {/* Email Templates row */}
        <div className="grid grid-cols-2 gap-x-4 mt-4">
          <div className="flex flex-col items-center -mr-12">
            <div 
              className={`w-[240px] h-[110px] bg-white border rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md ${
                uploadedFiles.emailTemplates ? 'border-green-400 bg-green-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('emailTemplates')}
            >
              {uploading.emailTemplates ? (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-[#00ADEF] border-t-transparent animate-spin mb-2"></div>
                  <p className="text-sm font-medium text-[#00ADEF]">Uploading...</p>
                </>
              ) : uploadedFiles.emailTemplates ? (
                <>
                  <div className="w-full h-[56px] mb-1 overflow-hidden rounded bg-gray-50 flex items-center justify-center text-xs text-gray-600">
                    {uploadedFiles.emailTemplates.name}
                  </div>
                  <p className="text-sm font-medium text-green-600">Uploaded</p>
                  <p className="text-xs text-green-500 truncate w-full">{uploadedFiles.emailTemplates.name}</p>
                </>
              ) : (
                <>
                  <img src={imgIcon} alt="upload" className="w-6 h-6 opacity-60 mb-2" />
                  <p className="text-sm font-medium text-gray-400">Upload File</p>
                </>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">Email Templates (HTML/ZIP)</p>
          </div>
        </div>
        
        {/* Bottom text */}
        <div className="text-center mt-6">
          <p className="text-base text-gray-400">
            Drag & Drop or <span className="text-blue-400 font-medium cursor-pointer">Browse</span> to Upload
          </p>
        </div>
      </div>
    </>
  );

  return (
    <div className="offerform">
      {loading === true && <Loader />}
      <BreadCrumsHeader
        heading="Add Marketing Assets"
        items={items}
        form={true}
      />
      {/* Render both sections: details and media */}
      <FormLayout title={"Add Marketing Assets"} content={<><div>{OfferInfoContent}</div><div className="mt-8">{MediaContent}</div></>} />

      <div className="col-span-12 text-end mt-14">
        <button
          className="w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#F0F0F0] text-[16px] font-[500] text-[#8C8C8C]"
          style={{ border: "1px solid #C4C4C4" }}
          onClick={() => {
            navigate("/promotions-list");
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#00ADEF] text-[16px] font-[500] text-[#FFFFFF] ms-2"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default AddAssetsForm;
 
 
